/**
 * Enum code ⇄ label maps — the frontend half of the contract in DATABASE.md §2.
 *
 * The backend stores every enumerated field as a SMALLINT code (and on/off flags
 * as BOOLEAN). The UI never string-compares status: it holds/sends the numeric
 * code and maps code → label with the *_LABEL tables here. Keep this file in sync
 * with backend/src/lib/constants.ts and DATABASE.md §2.
 */

export const USER_STATUS = { ACTIVE: 1, SUSPENDED: 2 } as const;
export const USER_STATUS_LABEL: Record<number, string> = { 1: 'Active', 2: 'Suspended' };

export const USER_ROLE = {
  PLAYER: 1,
  ADMIN: 2,
  OWNER: 3,
  GAME_FLOOR_MANAGER: 4,
  SUPPORT_AGENT: 5,
} as const;
export const USER_ROLE_LABEL: Record<number, string> = {
  1: 'Player',
  2: 'Admin',
  3: 'Owner',
  4: 'Game Floor Manager',
  5: 'Support Agent',
};

export const ADMIN_STATUS = { ACTIVE: 1, SUSPENDED: 2 } as const;
export const ADMIN_STATUS_LABEL: Record<number, string> = { 1: 'Active', 2: 'Suspended' };

export const TRANSACTION_STATUS = { PENDING: 1, COMPLETED: 2, FLAGGED: 3 } as const;
export const TRANSACTION_STATUS_LABEL: Record<number, string> = { 1: 'Pending', 2: 'Completed', 3: 'Flagged' };

export const TRANSACTION_TYPE = { DEPOSIT: 101, WITHDRAWAL: 102, HOUSE_RAKE: 103, POT_WIN: 104 } as const;
export const TRANSACTION_TYPE_LABEL: Record<number, string> = {
  101: 'Deposit',
  102: 'Withdrawal',
  103: 'HouseRake',
  104: 'PotWin',
};

export const BATTLE_TYPE = { PUBLIC: 1, PRIVATE: 2 } as const;
export const BATTLE_TYPE_LABEL: Record<number, string> = { 1: 'Public', 2: 'Private' };

export const BATTLE_STATUS = { WAITING: 1, ACTIVE: 2, COMPLETED: 3, ABANDONED: 4 } as const;
export const BATTLE_STATUS_LABEL: Record<number, string> = { 1: 'Running', 2: 'Active', 3: 'Completed', 4: 'Abandoned' };

export const PRIVATE_TABLE_STATUS = { ACTIVE: 1, CLOSED: 2 } as const;
export const PRIVATE_TABLE_STATUS_LABEL: Record<number, string> = { 1: 'Active', 2: 'Closed' };

export const BATTLE_PLAYER_STATUS = { ACTIVE: 1, FOLDED: 2, ALL_IN: 3, LEFT: 4 } as const;
export const BATTLE_PLAYER_STATUS_LABEL: Record<number, string> = { 1: 'Active', 2: 'Folded', 3: 'AllIn', 4: 'Left' };

export const ROUND_STATUS = { IN_PROGRESS: 1, COMPLETED: 2 } as const;
export const ROUND_STATUS_LABEL: Record<number, string> = { 1: 'InProgress', 2: 'Completed' };

export const ROUND_ACTION = { BET: 1, CALL: 2, CHECK: 3, RAISE: 4, FOLD: 5, ALL_IN: 6 } as const;
export const ROUND_ACTION_LABEL: Record<number, string> = {
  1: 'BET',
  2: 'CALL',
  3: 'CHECK',
  4: 'RAISE',
  5: 'FOLD',
  6: 'ALL_IN',
};

export const LEDGER_TYPE = {
  BUY_IN: 1,
  BET: 2,
  CALL: 3,
  CHECK: 4,
  RAISE: 5,
  ALL_IN: 6,
  WIN: 7,
  REFUND: 8,
  CASH_OUT: 9,
  LEAVE_TABLE: 10,
  ADMIN_ADJUSTMENT: 11,
} as const;
export const LEDGER_TYPE_LABEL: Record<number, string> = {
  1: 'BUY_IN',
  2: 'BET',
  3: 'CALL',
  4: 'CHECK',
  5: 'RAISE',
  6: 'ALL_IN',
  7: 'WIN',
  8: 'REFUND',
  9: 'CASH_OUT',
  10: 'LEAVE_TABLE',
  11: 'ADMIN_ADJUSTMENT',
};

/** Pre-migration admin wallet codes (1–4) before they were renumbered to 101–104. */
const LEGACY_ADMIN_TRANSACTION_TYPE_LABEL: Record<number, string> = {
  1: 'Deposit',
  2: 'Withdrawal',
  3: 'HouseRake',
  4: 'PotWin',
};

/** Map a numeric code to its label, tolerating null/string input. */
export const labelFor = (
  map: Record<number, string>,
  code: number | string | null | undefined
): string => (code === null || code === undefined || code === '' ? '—' : map[Number(code)] ?? String(code));

/** Teen Patti action labels (boot, blind, chaal, pack, …). */
export const GAME_ACTION_LABELS: Record<string, string> = {
  buy_in: 'Buy In',
  leave_table: 'Cash Out',
  win: 'Win',
  boot: 'Boot',
  shar: 'Shar',
  show: 'Show',
  side_show: 'Side Show',
  side_show_accept: 'Side Show Accept',
  side_show_reject: 'Side Show Reject',
  blind: 'Blind',
  blind2x: 'Blind 2×',
  chaal: 'Chaal',
  chaal2x: 'Chaal 2×',
  raise_blind: 'Raise Blind',
  raise_blind2x: 'Raise Blind 2×',
  raise_chaal: 'Raise Chaal',
  raise_chaal2x: 'Raise Chaal 2×',
  raise: 'Raise',
  fold: 'Pack',
  see: 'See',
};

/** In-game ledger codes → Teen Patti label when action_code is missing. */
const LEDGER_TYPE_TEEN_PATTI_LABEL: Record<number, string> = {
  [LEDGER_TYPE.BUY_IN]: 'Buy In',
  [LEDGER_TYPE.BET]: 'Blind',
  [LEDGER_TYPE.CALL]: 'Chaal',
  [LEDGER_TYPE.CHECK]: 'Pack',
  [LEDGER_TYPE.RAISE]: 'Raise',
  [LEDGER_TYPE.ALL_IN]: 'Show',
  [LEDGER_TYPE.WIN]: 'Win',
  [LEDGER_TYPE.REFUND]: 'Refund',
  [LEDGER_TYPE.CASH_OUT]: 'Cash Out',
  [LEDGER_TYPE.LEAVE_TABLE]: 'Cash Out',
  [LEDGER_TYPE.ADMIN_ADJUSTMENT]: 'Adjustment',
};

/** Resolve a round action or ledger row to a Teen Patti label. */
export const gameActionLabel = (
  actionCode?: string | null,
  roundAction?: number | string | null,
  transactionType?: number | string | null
): string => {
  if (actionCode) {
    if (GAME_ACTION_LABELS[actionCode]) return GAME_ACTION_LABELS[actionCode];
    return actionCode.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (roundAction != null && roundAction !== '') {
    switch (Number(roundAction)) {
      case ROUND_ACTION.FOLD:
      case ROUND_ACTION.CHECK:
        return 'Pack';
      case ROUND_ACTION.CALL:
        return 'Chaal';
      case ROUND_ACTION.RAISE:
        return 'Raise';
      case ROUND_ACTION.ALL_IN:
        return 'Show';
      case ROUND_ACTION.BET:
        return 'Blind';
      default:
        break;
    }
  }
  if (transactionType != null && transactionType !== '') {
    return transactionTypeLabel(Number(transactionType), null);
  }
  return '—';
};

export const roundActionLabel = (
  actionCode?: string | null,
  roundAction?: number | string | null
): string => gameActionLabel(actionCode, roundAction, null);

/** Unified label for admin (101–104 / legacy 1–4) and in-game (1–11 LEDGER_TYPE) rows. */
export const transactionTypeLabel = (
  type: number | string | null | undefined,
  actionCode?: string | null
): string => {
  if (type === null || type === undefined || type === '') return '—';
  const code = Number(type);
  if (TRANSACTION_TYPE_LABEL[code]) return TRANSACTION_TYPE_LABEL[code];
  if (LEGACY_ADMIN_TRANSACTION_TYPE_LABEL[code]) return LEGACY_ADMIN_TRANSACTION_TYPE_LABEL[code];
  if (actionCode && GAME_ACTION_LABELS[actionCode]) return GAME_ACTION_LABELS[actionCode];
  if (LEDGER_TYPE_TEEN_PATTI_LABEL[code]) return LEDGER_TYPE_TEEN_PATTI_LABEL[code];
  return LEDGER_TYPE_LABEL[code] ?? String(code);
};

export const isAdminTransactionType = (type: number | string | null | undefined): boolean => {
  const code = Number(type);
  return (code >= 101 && code <= 104) || (code >= 1 && code <= 4);
};

/** Tailwind pill classes keyed by a status *label* (works across battle / round /
 *  player status enums since their labels don't collide). */
export const STATUS_PILL: Record<string, string> = {
  Active: 'bg-green-500/10 text-green-700 border-green-200',
  Running: 'bg-amber-500/10 text-amber-700 border-amber-200',
  Completed: 'bg-blue-500/10 text-blue-700 border-blue-200',
  InProgress: 'bg-amber-500/10 text-amber-700 border-amber-200',
  Folded: 'bg-red-500/10 text-red-700 border-red-200',
  AllIn: 'bg-indigo-500/10 text-indigo-700 border-indigo-200',
  Left: 'bg-subtle text-muted border-line',
  Abandoned: 'bg-red-500/10 text-red-700 border-red-200',
  Closed: 'bg-subtle text-muted border-line',
};
export const statusPillByLabel = (label: string): string =>
  STATUS_PILL[label] ?? 'bg-subtle text-muted border-line';
